import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Upload, Star, Package, Image as ImageIcon } from "lucide-react";

const emptyProduct = {
  name: "", description: "", price: 0, currency: "GBP", image: "", images: [],
  sizes: ["S", "M", "L", "XL"], in_stock: true, featured: false,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [branding, setBranding] = useState({ logo: "", tiktok_handle: "", site_tagline: "" });
  const [editing, setEditing] = useState(null); // null | {} for new | product for edit
  const [uploading, setUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  const loadProducts = () => api.get("/products").then(r => setProducts(r.data));
  const loadOrders = () => api.get("/admin/orders").then(r => setOrders(r.data)).catch(() => setOrders([]));
  const loadBranding = () => api.get("/admin/branding").then(r => setBranding(r.data)).catch(() => {});

  useEffect(() => {
    // verify auth
    api.get("/auth/me").catch(() => navigate("/admin/login"));
    loadProducts();
    loadOrders();
    loadBranding();
  }, []); // eslint-disable-line

  const logout = () => {
    localStorage.removeItem("cmc_token");
    navigate("/admin/login");
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const currentImages = editing.images || (editing.image ? [editing.image] : []);
    const slotsLeft = 5 - currentImages.length;
    if (slotsLeft <= 0) {
      toast.error("Maximum 5 images per product");
      return;
    }
    const toUpload = files.slice(0, slotsLeft);
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of toUpload) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(res.data.image);
      }
      const newImages = [...currentImages, ...uploaded].slice(0, 5);
      setEditing({ ...editing, images: newImages, image: newImages[0] });
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = ""; // reset input
    }
  };

  const removeImage = (idx) => {
    const cur = editing.images || (editing.image ? [editing.image] : []);
    const next = cur.filter((_, i) => i !== idx);
    setEditing({ ...editing, images: next, image: next[0] || "" });
  };

  const setPrimary = (idx) => {
    const cur = editing.images || [];
    if (idx === 0) return;
    const next = [cur[idx], ...cur.filter((_, i) => i !== idx)];
    setEditing({ ...editing, images: next, image: next[0] });
  };

  const saveProduct = async () => {
    try {
      const payload = { ...editing, price: parseFloat(editing.price) || 0 };
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload);
      } else {
        await api.post("/admin/products", payload);
      }
      toast.success("Saved");
      setEditing(null);
      loadProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const removeProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    loadProducts();
  };

  return (
    <div data-testid="admin-dashboard" className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="overline">Admin</div>
          <h1 className="font-serif text-4xl mt-2">Dashboard</h1>
        </div>
        <button onClick={logout} data-testid="admin-logout-btn" className="text-sm tracking-[0.2em] uppercase hover:text-brand-accent inline-flex items-center gap-2">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      <div className="flex gap-1 border-b border-brand-line mb-8">
        <TabBtn active={tab === "products"} onClick={() => setTab("products")} testid="tab-products">
          <Package size={14} /> Products ({products.length})
        </TabBtn>
        <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} testid="tab-orders">
          Orders ({orders.length})
        </TabBtn>
        <TabBtn active={tab === "branding"} onClick={() => setTab("branding")} testid="tab-branding">
          <ImageIcon size={14} /> Branding
        </TabBtn>
      </div>

      {tab === "products" && (
        <>
          <div className="flex justify-end mb-6">
            <button onClick={() => setEditing({ ...emptyProduct })} data-testid="add-product-btn" className="btn-primary inline-flex items-center gap-2">
              <Plus size={14} /> Add product
            </button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className="border border-brand-line bg-brand-bg p-4">
                <div className="aspect-[3/4] overflow-hidden bg-brand-bg2 relative">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  {p.featured && (
                    <div className="absolute top-2 right-2 bg-brand-accent text-white text-[10px] tracking-[0.2em] uppercase px-2 py-1 inline-flex items-center gap-1">
                      <Star size={10} fill="currentColor" /> Featured
                    </div>
                  )}
                </div>
                <div className="mt-3 flex items-start justify-between gap-2">
                  <div>
                    <div className="font-serif text-lg">{p.name}</div>
                    <div className="text-sm text-brand-muted">£{p.price.toFixed(2)}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setEditing({ ...p, images: p.images && p.images.length > 0 ? p.images : (p.image ? [p.image] : []) })} aria-label="Edit" data-testid={`edit-${p.id}`} className="p-2 hover:bg-brand-bg2"><Pencil size={14} /></button>
                    <button onClick={() => removeProduct(p.id)} aria-label="Delete" data-testid={`delete-${p.id}`} className="p-2 hover:bg-brand-bg2 hover:text-destructive"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "orders" && (
        <div className="space-y-4">
          {orders.length === 0 && <div className="text-brand-muted py-12 text-center">No orders yet.</div>}
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="border border-brand-line bg-brand-bg p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-brand-muted">{new Date(o.created_at).toLocaleString()}</div>
                  <div className="font-serif text-lg mt-1">{o.customer.name} · £{o.amount.toFixed(2)}</div>
                  <div className="text-sm text-brand-muted">{o.customer.email} · {o.customer.address}</div>
                </div>
                <div className="text-right text-xs uppercase tracking-[0.2em]">
                  <div className={`px-2 py-1 ${o.status === "paid" ? "bg-brand-accent text-white" : "bg-brand-bg2 text-brand-muted"}`}>
                    {o.status} · {o.payment_method}
                  </div>
                </div>
              </div>
              <ul className="mt-4 space-y-1 text-sm text-brand-muted">
                {o.items.map((i, idx) => (
                  <li key={idx}>· {i.quantity}× {i.name} ({i.size}) — £{(i.price * i.quantity).toFixed(2)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {tab === "branding" && (
        <div className="max-w-2xl space-y-6" data-testid="branding-panel">
          <div>
            <div className="overline mb-3">Shop logo</div>
            <p className="text-sm text-brand-muted mb-4">Upload your logo (PNG with transparent background works best). It will replace the text logo in the header.</p>
            <div className="flex items-start gap-6 p-6 border border-brand-line bg-brand-bg">
              {branding.logo ? (
                <img src={branding.logo} alt="Logo" className="h-20 object-contain bg-brand-bg2 p-2" />
              ) : (
                <div className="h-20 w-32 border border-dashed border-brand-line flex items-center justify-center text-xs text-brand-muted">No logo</div>
              )}
              <div className="flex-1 space-y-3">
                <label className="btn-outline cursor-pointer inline-flex items-center gap-2 text-xs">
                  <Upload size={14} /> {logoUploading ? "Uploading…" : "Upload logo"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setLogoUploading(true);
                      try {
                        const fd = new FormData();
                        fd.append("file", file);
                        const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        setBranding({ ...branding, logo: res.data.image });
                        toast.success("Logo ready — click Save to apply.");
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Upload failed");
                      } finally {
                        setLogoUploading(false);
                      }
                    }}
                    className="hidden"
                    data-testid="logo-upload"
                  />
                </label>
                {branding.logo && (
                  <button
                    onClick={() => setBranding({ ...branding, logo: "" })}
                    className="text-xs text-brand-muted hover:text-destructive ml-3"
                    data-testid="remove-logo"
                  >
                    Remove logo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="overline block mb-2">TikTok handle (without @)</label>
            <input
              type="text"
              value={branding.tiktok_handle}
              onChange={(e) => setBranding({ ...branding, tiktok_handle: e.target.value })}
              placeholder="cindymarkscollections"
              data-testid="branding-tiktok"
              className="w-full border border-brand-line bg-brand-bg px-4 py-3 text-sm focus:outline-none focus:border-brand-ink"
            />
          </div>

          <div>
            <label className="overline block mb-2">Site tagline (optional)</label>
            <input
              type="text"
              value={branding.site_tagline}
              onChange={(e) => setBranding({ ...branding, site_tagline: e.target.value })}
              placeholder="Hand-tailored Ankara — made in small batches"
              data-testid="branding-tagline"
              className="w-full border border-brand-line bg-brand-bg px-4 py-3 text-sm focus:outline-none focus:border-brand-ink"
            />
          </div>

          <button
            onClick={async () => {
              try {
                await api.put("/admin/branding", branding);
                toast.success("Branding saved. Refresh the homepage to see changes.");
              } catch (e) {
                toast.error("Save failed");
              }
            }}
            data-testid="save-branding-btn"
            className="btn-primary"
          >
            Save branding
          </button>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-brand-bg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8" data-testid="product-modal">
            <div className="overline">{editing.id ? "Edit product" : "New product"}</div>
            <h2 className="font-serif text-3xl mt-2 mb-6">{editing.id ? editing.name : "Add a new piece"}</h2>
            <div className="space-y-4">
              <Input label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="prod-name" />
              <Input label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="prod-desc" textarea />
              <div className="grid sm:grid-cols-2 gap-4">
                <Input label="Price (GBP £)" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} testid="prod-price" />
                <Input label="Sizes (comma separated)" value={editing.sizes.join(", ")} onChange={(v) => setEditing({ ...editing, sizes: v.split(",").map(s => s.trim()).filter(Boolean) })} testid="prod-sizes" />
              </div>
              <div>
                <label className="overline block mb-2">Product photos (up to 5)</label>
                <p className="text-xs text-brand-muted mb-3">First photo is the main image shown on shop grid. Click ⭐ to make any photo the main.</p>
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {(editing.images && editing.images.length > 0 ? editing.images : (editing.image ? [editing.image] : [])).map((src, idx) => (
                    <div key={idx} className="relative aspect-[3/4] border border-brand-line bg-brand-bg2 overflow-hidden group" data-testid={`admin-img-${idx}`}>
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      {idx === 0 && (
                        <div className="absolute top-1 left-1 bg-brand-accent text-white text-[9px] tracking-[0.15em] uppercase px-1.5 py-0.5">Main</div>
                      )}
                      <div className="absolute inset-0 bg-brand-ink/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                        {idx !== 0 && (
                          <button type="button" onClick={() => setPrimary(idx)} className="text-brand-gold text-[10px] tracking-widest uppercase hover:text-brand-bg" data-testid={`set-primary-${idx}`}>
                            ★ Make main
                          </button>
                        )}
                        <button type="button" onClick={() => removeImage(idx)} className="text-brand-bg text-[10px] tracking-widest uppercase hover:text-destructive" data-testid={`remove-img-${idx}`}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                  {((editing.images?.length ?? (editing.image ? 1 : 0)) < 5) && (
                    <label className="aspect-[3/4] border-2 border-dashed border-brand-line flex flex-col items-center justify-center cursor-pointer hover:bg-brand-bg2 transition-colors text-center p-2">
                      <Upload size={16} className="text-brand-muted mb-1" />
                      <span className="text-[10px] tracking-[0.15em] uppercase text-brand-muted">{uploading ? "Uploading…" : "Add photo"}</span>
                      <input type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="prod-upload" />
                    </label>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="Or paste an image URL and press Enter to add"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.currentTarget.value.trim()) {
                      e.preventDefault();
                      const cur = editing.images && editing.images.length > 0 ? editing.images : (editing.image ? [editing.image] : []);
                      if (cur.length >= 5) { toast.error("Maximum 5 images"); return; }
                      const next = [...cur, e.currentTarget.value.trim()].slice(0, 5);
                      setEditing({ ...editing, images: next, image: next[0] });
                      e.currentTarget.value = "";
                    }
                  }}
                  data-testid="prod-image-url"
                  className="w-full border border-brand-line bg-brand-bg px-3 py-2 text-xs"
                />
              </div>
              <div className="flex flex-wrap gap-6">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.in_stock} onChange={(e) => setEditing({ ...editing, in_stock: e.target.checked })} data-testid="prod-instock" />
                  In stock
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="prod-featured" />
                  Featured on homepage
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={() => setEditing(null)} className="btn-outline" data-testid="cancel-edit-btn">Cancel</button>
              <button onClick={saveProduct} className="btn-primary" data-testid="save-product-btn">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-5 py-3 text-sm tracking-[0.2em] uppercase inline-flex items-center gap-2 border-b-2 -mb-px ${
        active ? "border-brand-ink text-brand-ink" : "border-transparent text-brand-muted hover:text-brand-ink"
      }`}
    >
      {children}
    </button>
  );
}

function Input({ label, value, onChange, type = "text", testid, textarea }) {
  const Cmp = textarea ? "textarea" : "input";
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      <Cmp
        type={type}
        rows={textarea ? 4 : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full border border-brand-line bg-brand-bg px-4 py-3 text-sm focus:outline-none focus:border-brand-ink"
      />
    </div>
  );
}
